/**
 * Levantar una observación: cómo se resolvió + foto del resultado.
 * Reescrito en JSX legible el 2026-10-07: botones claros para tomar la foto
 * con la cámara o elegirla de la galería, con vista previa antes de enviar.
 * La imagen se comprime antes de subir (z$) y se sube con tope de tiempo (Za).
 */
import {
  rec_AccionAsync as AccionAsync,
  rec_EstadoError as EstadoError,
  rec_React as React,
  rec_Za as subir,
  rec_z$ as comprimir,
} from "../../runtime/index.jsx";
import "../observaciones/observaciones.css";

// runtime/index.jsx importa este archivo antes de definir React (import circular).
const useEffect = (...a) => React.useEffect(...a);
const useRef = (...a) => React.useRef(...a);
const useState = (...a) => React.useState(...a);

export function ResolverObservacion({ observacion, onListo, onCancelar }) {
  const [solucion, setSolucion] = useState(``);
  const [foto, setFoto] = useState(null);
  const [vista, setVista] = useState(``);
  const [error, setError] = useState(``);
  const [enviando, setEnviando] = useState(false);
  const camara = useRef(null);
  const galeria = useRef(null);

  useEffect(() => {
    if (!foto) return setVista(``);
    const url = URL.createObjectURL(foto);
    setVista(url);
    return () => URL.revokeObjectURL(url);
  }, [foto]);

  const elegir = (e) => {
    const f = e.target.files?.[0];
    e.target.value = ``;
    if (!f) return;
    if (!/^image\/(jpeg|png|webp)$/.test(f.type)) return setError(`La foto tiene que ser JPG, PNG o WEBP`);
    setError(``);
    setFoto(f);
  };

  async function enviar() {
    if (enviando) return;
    setEnviando(true);
    setError(``);
    try {
      const datos = new FormData();
      datos.append(`solucion`, solucion);
      datos.append(`archivo`, await comprimir(foto));
      await subir(`/inspecciones/observaciones/${observacion.id}/resolver`, datos, { topeMs: 6e4 });
      await onListo();
    } catch (e) {
      setError(e.message);
      setEnviando(false);
    }
  }

  return (
    <div className="animar-entrada flex flex-col gap-3">
      <EstadoError mensaje={error} />
      <label className="block text-sm text-(--text-h)">
        ¿Cómo se levantó?
        <textarea value={solucion} maxLength={1000} onChange={(e) => setSolucion(e.target.value)} autoFocus
          placeholder="Ej.: Se entregaron cofias nuevas y se verificó su uso al inicio del turno."
          className="mt-1 min-h-20 w-full rounded-xl border border-(--border) bg-(--bg) p-3 text-base" />
      </label>

      <div className="flex flex-col gap-2">
        <span className="text-sm text-(--text-h)">Foto del resultado</span>
        <input ref={camara} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" hidden onChange={elegir} />
        <input ref={galeria} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={elegir} />
        {vista ? (
          <div className="ob-foto">
            <img src={vista} alt="Foto del resultado" />
            <div className="flex flex-wrap gap-2">
              <AccionAsync variante="chico" onClick={() => camara.current?.click()}>📷 Tomar otra</AccionAsync>
              <AccionAsync variante="chico" onClick={() => galeria.current?.click()}>🖼 Elegir otra</AccionAsync>
              <AccionAsync variante="chico" onClick={() => setFoto(null)}>Quitar</AccionAsync>
            </div>
          </div>
        ) : (
          <div className="ob-subir">
            <button type="button" onClick={() => camara.current?.click()}>
              <span aria-hidden="true">📷</span>Tomar foto
            </button>
            <button type="button" onClick={() => galeria.current?.click()}>
              <span aria-hidden="true">🖼</span>Elegir de la galería
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <AccionAsync className="min-h-11" disabled={!solucion.trim() || !foto || enviando} onClick={enviar}>
          {enviando ? `Enviando…` : `Registrar solución`}
        </AccionAsync>
        <AccionAsync variante="secundario" className="min-h-11" disabled={enviando} onClick={onCancelar}>Cancelar</AccionAsync>
      </div>
      {(!solucion.trim() || !foto) && (
        <p className="text-xs text-(--text)">Para registrar: describe cómo se levantó y agrega una foto.</p>
      )}
    </div>
  );
}
